import { action, makeObservable, observable } from 'mobx';

import Logger from 'utils/logger';
import { publicEventFormDB } from 'utils/indexedDB/publicEventFormDB';

const LOG_PREFIX = '[FileUploadManager]';

export enum FileUploadStatus {
  Pending = 'pending',
  Uploading = 'uploading',
  Success = 'success',
  Failed = 'failed',
  Deleting = 'deleting',
}

export interface FileUploadItem {
  file: File;
  status: FileUploadStatus;
  fieldKey: string;
}

/**
 * Manages file upload state for form fields.
 * Provides a clean API for tracking files pending upload and their states.
 */
export default class FileUploadManager {
  // Simple flat map: "fieldKey:filename" -> FileUploadItem
  @observable private uploads: Map<string, FileUploadItem> = new Map();

  // Session ID for IndexedDB persistence
  private sessionId: string | null = null;

  constructor() {
    makeObservable(this);
  }

  @action.bound
  setSessionId(sessionId: string): void {
    this.sessionId = sessionId;
  }

  getSessionId(): string | null {
    return this.sessionId;
  }

  private getKey(fieldKey: string, filename: string): string {
    return `${fieldKey}:${filename}`;
  }

  @action.bound
  async addFiles(fieldKey: string, files: File[]): Promise<void> {
    Logger.debug(
      `${LOG_PREFIX} addFiles: fieldKey=${fieldKey}, files=[${files.map((f) => f.name).join(', ')}], ` +
        `currentUploadsCount=${this.uploads.size}`
    );

    // First, add all files to the uploads map synchronously
    for (const file of files) {
      const key = this.getKey(fieldKey, file.name);
      this.uploads.set(key, {
        file,
        status: FileUploadStatus.Pending,
        fieldKey,
      });
    }

    // Then persist to IndexedDB in parallel if we have a session
    if (this.sessionId) {
      const persistPromises = files.map((file) =>
        publicEventFormDB.storeAttachmentFile(this.sessionId!, fieldKey, file).catch((error) => {
          Logger.error(`${LOG_PREFIX} Failed to persist file to IndexedDB:`, error);
        })
      );
      await Promise.all(persistPromises);
    }

    Logger.debug(`${LOG_PREFIX} addFiles complete: newUploadsCount=${this.uploads.size}`);
  }

  @action.bound
  updateStatus(fieldKey: string, filename: string, status: FileUploadStatus): void {
    const key = this.getKey(fieldKey, filename);
    const item = this.uploads.get(key);
    if (item) {
      item.status = status;
    }
  }

  @action.bound
  async removeFile(fieldKey: string, filename: string): Promise<void> {
    const key = this.getKey(fieldKey, filename);
    const existed = this.uploads.has(key);
    this.uploads.delete(key);
    Logger.debug(
      `${LOG_PREFIX} removeFile: fieldKey=${fieldKey}, filename=${filename}, existed=${existed}, ` +
        `remainingUploadsCount=${this.uploads.size}`
    );

    // Remove from IndexedDB if we have a session
    if (this.sessionId) {
      try {
        await publicEventFormDB.deleteAttachmentFile(this.sessionId, fieldKey, filename);
      } catch (error) {
        Logger.error(`${LOG_PREFIX} Failed to remove file from IndexedDB:`, error);
      }
    }
  }

  @action.bound
  async clearField(fieldKey: string): Promise<void> {
    const keysToDelete = Array.from(this.uploads.keys()).filter((key) => key.startsWith(`${fieldKey}:`));
    Logger.debug(
      `${LOG_PREFIX} clearField: fieldKey=${fieldKey}, keysToDelete=[${keysToDelete.join(', ')}], ` +
        `currentUploadsCount=${this.uploads.size}`
    );
    keysToDelete.forEach((key) => this.uploads.delete(key));
    Logger.debug(`${LOG_PREFIX} clearField complete: remainingUploadsCount=${this.uploads.size}`);

    // Clear from IndexedDB if we have a session
    if (this.sessionId) {
      try {
        await publicEventFormDB.deleteFieldAttachmentFiles(this.sessionId, fieldKey);
      } catch (error) {
        Logger.error(`${LOG_PREFIX} Failed to clear field files from IndexedDB:`, error);
      }
    }
  }

  @action.bound
  async clearAll(): Promise<void> {
    this.uploads.clear();

    // Clear from IndexedDB if we have a session
    if (this.sessionId) {
      try {
        await publicEventFormDB.deleteAllAttachmentFiles(this.sessionId);
      } catch (error) {
        Logger.error(`${LOG_PREFIX} Failed to clear all files from IndexedDB:`, error);
      }
    }
  }

  /**
   * Restore files from IndexedDB for a given session
   */
  @action.bound
  async restoreFromIndexedDB(sessionId: string): Promise<void> {
    this.sessionId = sessionId;

    try {
      const storedFiles = await publicEventFormDB.getAttachmentFiles(sessionId);

      for (const { fieldKey, file } of storedFiles) {
        const key = this.getKey(fieldKey, file.name);
        this.uploads.set(key, {
          file,
          status: FileUploadStatus.Pending,
          fieldKey,
        });
      }

      Logger.debug(`${LOG_PREFIX} Restored ${storedFiles.length} files from IndexedDB for session: ${sessionId}`);
    } catch (error) {
      Logger.error(`${LOG_PREFIX} Failed to restore files from IndexedDB:`, error);
    }
  }

  @action.bound
  clearAllDeletingFiles(): void {
    Array.from(this.uploads.values())
      .filter((item) => item.status === FileUploadStatus.Deleting)
      .forEach((item) => this.uploads.delete(this.getKey(item.fieldKey, item.file.name)));
  }

  /**
   * Get all uploads for a specific field
   */
  getFieldUploads(fieldKey: string): FileUploadItem[] {
    return Array.from(this.uploads.values()).filter((item) => item.fieldKey === fieldKey);
  }

  /**
   * Get all files for a specific field (regardless of status)
   */
  getFieldFiles(fieldKey: string): File[] {
    return this.getFieldUploads(fieldKey).map((item) => item.file);
  }

  /**
   * Get the status of a specific file
   */
  getFileStatus(fieldKey: string, filename: string): FileUploadStatus | undefined {
    const key = this.getKey(fieldKey, filename);
    return this.uploads.get(key)?.status;
  }

  /**
   * Check if a field has any uploads
   */
  hasFieldUploads(fieldKey: string): boolean {
    return this.getFieldUploads(fieldKey).length > 0;
  }

  /**
   * Get all pending files across all fields
   */
  getAllPendingFiles(): File[] {
    const files = Array.from(this.uploads.values()).map((item) => item.file);
    Logger.debug(
      `${LOG_PREFIX} getAllPendingFiles: returning ${files.length} files, ` +
        `filenames=[${files.map((f) => f.name).join(', ')}]`
    );
    return files;
  }

  /**
   * Check if any file in a field is in deleting state
   */
  isFieldDeleting(fieldKey: string): boolean {
    return this.getFieldUploads(fieldKey).some((item) => item.status === FileUploadStatus.Deleting);
  }
}
